import { Mutation, SellerListBuilderExportTargetInput, SellerListBuilderExportType, SellerListBuilderPreviewInput } from '__generated__/graphql'
import { Effect, RequestResolver, pipe } from 'effect'
import { UnknownException } from 'effect/Cause'
import { MarketingList, MarketingListId } from 'features/ListBuilder/domain/MarketingList'
import { ListType } from 'features/ListBuilder/domain/MarketingList/ListType'
import MarketingListRequest from 'features/ListBuilder/infra/repo/MarketingListRepoLive/MarketingListRequest'
import { SELLER_LIST_BUILDER_EXPORT_MUTATION } from 'features/ListBuilder/infra/repo/MarketingListRepoLive/SaveListResolver/schema'
import CriteriaSchema from 'features/ListBuilder/infra/repo/schema/CriteriaSchema'
import { apolloClient } from 'presentation/libs/client'

const toDomain = (
  request: MarketingListRequest.SaveList,
  response: Mutation['sellerListBuilderExport'],
): MarketingList =>
  MarketingList.make({
    id: MarketingListId.make(response.marketingListId),
    type: mapExportListTypeToDomain(response.listType),
    name: request.listName,
    createdAt: new Date(),
    updatedAt: new Date(),
    criteria: request.criteria,
  })

const SaveListResolver = RequestResolver.fromEffect(
  (request: MarketingListRequest.SaveList) => pipe(
    Effect.sync(() =>
      CriteriaSchema.toSellerListBuilderPreviewInput(request.criteria),
    ),
    Effect.flatMap(input =>
      Effect.tryPromise({
        try: async () =>
          await apolloClient.mutate<
            SellerListBuilderExportResponse,
            SellerListBuilderExportVariables
          >({
            mutation: SELLER_LIST_BUILDER_EXPORT_MUTATION as any,
            variables: {
              input,
              target: {
                type: SellerListBuilderExportType.Seller,
                name: request.listName,
              },
            },
          }),
        catch: error => new UnknownException({ message: String(error) }),
      }),
    ),
    Effect.flatMap(result =>
      result.data?.sellerListBuilderExport
        ? Effect.succeed(toDomain(request, result.data.sellerListBuilderExport))
        : Effect.dieMessage('mutation SellerListBuilderExport failed'),
    ),
    Effect.catchTag('UnknownException', v => Effect.die(v)),
  ),
)

export default SaveListResolver

type SellerListBuilderExportResponse = Pick<Mutation, 'sellerListBuilderExport'>
type SellerListBuilderExportVariables = {
  input: SellerListBuilderPreviewInput
  target: SellerListBuilderExportTargetInput
}

const mapExportListTypeToDomain = (listType: string): ListType => {
  const normalized = listType.toLowerCase().trim()

  if (normalized.includes('cash')) return 'cash-buyers'
  if (normalized.includes('private')) return 'private-lenders'
  return 'property-lists'
}
